import { useEffect, useState } from "react";
import {
  Popover,
  Button,
  Checkbox,
  TextField,
  Typography,
  InputAdornment,
  Divider,
  Box,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useColumnFilterContext } from "../../Utils/useColumnFilters";
import { Vocabulary } from "../../Utils/Vocabulary";

export default function ColumnFilterPopover() {
  const {
    anchorEl,
    activeColumn,
    searchText,
    setSearchText,
    closeFilter,
    getFilterOptions,
    applyFilter,
    currentValues,
  } = useColumnFilterContext();

  const [selectedValues, setSelectedValues] = useState<string[]>([]);

  // Reset selection when opening a different column's filter
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSelectedValues(currentValues);
  }, [activeColumn?.name]);

  const LARGE_LIST_THRESHOLD = 50;

  const options = getFilterOptions();
  const isLargeList = options.length > LARGE_LIST_THRESHOLD && activeColumn?.urlParam === "model";
  const showHint = isLargeList && searchText.length === 0 && selectedValues.length === 0;
  const selectedSet = new Set(selectedValues);
  const filteredOptions = showHint
    ? []
    : searchText.length === 0
      ? isLargeList
        ? options.filter((o) => selectedSet.has(o.value))
        : options
      : options.filter(
          (o) =>
            o.label.toLowerCase().includes(searchText.toLowerCase()) ||
            selectedSet.has(o.value),
        );

  const allSelected =
    options.length > 0 && options.every((o) => selectedValues.includes(o.value));
  const someSelected = selectedValues.length > 0 && !allSelected;

  const toggleValue = (value: string) => {
    setSelectedValues((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={closeFilter}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      PaperProps={{ style: { minWidth: 220 } }}
    >
      <Box style={{ padding: "8px 8px 4px" }}>
        <TextField
          size="small"
          fullWidth
          placeholder={Vocabulary.search}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {!showHint && !isLargeList && (
        <>
          <Divider />
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 8px",
            }}
          >
            <Box
              style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
              onClick={() =>
                allSelected
                  ? setSelectedValues([])
                  : setSelectedValues(options.map((o) => o.value))
              }
            >
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onClick={(e) => e.stopPropagation()}
                onChange={() =>
                  allSelected
                    ? setSelectedValues([])
                    : setSelectedValues(options.map((o) => o.value))
                }
              />
              <Typography variant="body2">{Vocabulary.selectAll}</Typography>
            </Box>
            <Button size="small" variant="text" onClick={() => setSelectedValues([])}>
              {Vocabulary.clearAll}
            </Button>
          </Box>
          <Divider />
        </>
      )}

      <Box style={{ maxHeight: 260, overflowY: "auto" }}>
        {showHint ? (
          <Typography
            variant="body2"
            style={{ padding: "12px 16px", color: "rgba(0,0,0,0.5)" }}
          >
            {Vocabulary.typeToSearchOptions}
          </Typography>
        ) : (
          filteredOptions.map((option) => (
            <Box
              key={option.value}
              style={{ display: "flex", alignItems: "center", padding: "0 8px", cursor: "pointer" }}
              onClick={() => toggleValue(option.value)}
            >
              <Checkbox
                size="small"
                checked={selectedValues.includes(option.value)}
                onClick={(e) => e.stopPropagation()}
                onChange={() => toggleValue(option.value)}
              />
              <Typography variant="body2">{option.label}</Typography>
            </Box>
          ))
        )}
      </Box>

      <Divider />

      <Box style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: 8 }}>
        <Button size="small" variant="text" style={{ color: "#FF6633" }} onClick={closeFilter}>
          {Vocabulary.cancel}
        </Button>
        <Button
          size="small"
          variant="contained"
          style={{ backgroundColor: "#FF6633", color: "#fff" }}
          onClick={() => applyFilter(selectedValues)}
        >
          {Vocabulary.apply}
        </Button>
      </Box>
    </Popover>
  );
}
